import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Landmark, Factory, Building2, Crown, Shield, Sparkles, Castle,
  BookOpen, Home, Zap, ExternalLink, PlayCircle,
} from "lucide-react";

import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter } from "@/components/site/FinalCTA";
import { FAMOUS_FAMILIES, HISTORIC_ROYAL_HOUSES, PROFILE_FIELDS } from "@/data/famousFamilies";
import { FAMOUS } from "@/constants/testIds";

const ICONS = {
  Landmark, Factory, Building2, Crown, Shield, Sparkles, Castle,
  BookOpen, Home, Zap,
};

function FamilyCard({ f, i }) {
  const Icon = ICONS[f.icon] || Landmark;
  const alt = i % 2 === 1;

  return (
    <motion.article
      data-testid={FAMOUS.card(f.id)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="border-b border-ivory-300 py-16 lg:py-24"
    >
      <div className={`grid grid-cols-12 gap-8 md:gap-12 items-start ${alt ? "" : ""}`}>
        {/* Portrait */}
        <div className={`col-span-12 md:col-span-5 ${alt ? "md:order-2" : ""}`}>
          <div className="relative">
            <div className="arch overflow-hidden border border-ivory-300 spotlight max-w-[380px]">
              <img
                src={f.portrait}
                alt={f.name}
                referrerPolicy="no-referrer"
                onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1732303105237-f5b5f7a2390e?crop=entropy&cs=srgb&fm=jpg&w=800&q=85"; }}
                className="w-full h-[420px] object-cover grayscale-[0.15] sepia-[0.15]"
              />
            </div>
            <span className="absolute -left-2 -top-2 w-4 h-4 border-l border-t border-ink/60" />
            <span className="absolute -right-2 -top-2 w-4 h-4 border-r border-t border-ink/60" style={{ right: "calc(100% - 380px - 8px)" }} />
            <div className="mt-3 font-serif italic text-ink-50 text-sm max-w-[380px] flex items-center justify-between">
              <span>{f.origin}</span>
              <span className="text-[10px] uppercase tracking-[0.24em]">{f.type}</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={`col-span-12 md:col-span-7 ${alt ? "md:order-1" : ""}`}>
          <div className="flex items-center gap-4">
            <div className="font-serif text-2xl md:text-3xl text-saffron">{f.number}</div>
            <span
              className="inline-flex items-center justify-center w-10 h-10 rounded-full text-ivory"
              style={{ backgroundColor: f.accent }}
            >
              <Icon size={18} strokeWidth={1.6} />
            </span>
            <span className="text-[10px] tracking-[0.28em] uppercase text-ink-50">{f.community}</span>
          </div>

          <h2 className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-ink leading-[1.02]">
            <Link to={`/famous-families/${f.id}`} className="hover:text-saffron transition">
              {f.name}
            </Link>
          </h2>

          <p className="mt-5 text-ink-50 text-lg font-light leading-relaxed max-w-2xl">
            {f.legacy}
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Known for</div>
              <div className="mt-2 text-ink">{f.known_for}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Origin</div>
              <div className="mt-2 text-ink">{f.origin}</div>
            </div>
          </div>

          {/* Lineage */}
          <div className="mt-8">
            <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Family lineage</div>
            <ol className="mt-3 flex flex-wrap items-center gap-2">
              {f.lineage.map((person, idx) => (
                <React.Fragment key={person}>
                  <li className="font-serif italic text-ink text-lg md:text-xl">{person}</li>
                  {idx < f.lineage.length - 1 && (
                    <span className="text-saffron">↓</span>
                  )}
                </React.Fragment>
              ))}
            </ol>
          </div>

          {(f.patriarchs.length > 0 || f.matriarchs.length > 0) && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {f.patriarchs.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Patriarchs</div>
                  <ul className="mt-2 space-y-1 text-ink">
                    {f.patriarchs.map((p) => <li key={p}>· {p}</li>)}
                  </ul>
                </div>
              )}
              {f.matriarchs.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-ink-50">Matriarchs</div>
                  <ul className="mt-2 space-y-1 text-ink">
                    {f.matriarchs.map((p) => <li key={p}>· {p}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/famous-families/${f.id}`}
              className="inline-flex items-center gap-2 rounded-full bg-tulsi text-ivory px-5 py-2.5 text-sm font-medium hover:bg-tulsi-deep transition"
            >
              Full profile →
            </Link>
            <a
              href={f.wiki}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={FAMOUS.wikiLink(f.id)}
              className="inline-flex items-center gap-2 rounded-full border border-ink/25 text-ink px-5 py-2.5 text-sm hover:border-ink hover:bg-ink hover:text-ivory transition"
            >
              <ExternalLink size={14} /> Wikipedia
            </a>
            <a
              href={f.video}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={FAMOUS.videoLink(f.id)}
              className="inline-flex items-center gap-2 rounded-full bg-saffron text-ivory px-5 py-2.5 text-sm hover:bg-saffron-deep transition"
            >
              <PlayCircle size={16} /> Watch on YouTube
            </a>
          </div>
        </div>
      </div>
    </motion.article>
  );
}

export default function FamousFamiliesPage() {
  return (
    <div data-testid={FAMOUS.root} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="A SocialPulse Heritage Project"
        title="India's Great Families —"
        italic="lineages"
        tail="that shaped a civilisation."
        subtitle="India's history is often told through empires, movements, literature, science, business and culture. Behind these achievements stand remarkable families whose influence has extended across generations. Some trace their roots back over a thousand years; others emerged during India's modern industrial and democratic eras. Together, these lineages tell the story of a nation built on tradition, resilience, innovation and service."
      />

      <section className="relative py-16">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {/* Icon-forward index */}
          <div>
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">The index</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-ink">
              Ten lineages, in order of appearance.
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {FAMOUS_FAMILIES.map((f) => {
              const Icon = ICONS[f.icon] || Landmark;
              return (
                <Link
                  key={f.id}
                  to={`/famous-families/${f.id}`}
                  className="group border border-ivory-300 rounded-2xl p-5 bg-ivory hover:border-ink transition"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="inline-flex items-center justify-center w-10 h-10 rounded-full text-ivory shrink-0"
                      style={{ backgroundColor: f.accent }}
                    >
                      <Icon size={16} strokeWidth={1.6} />
                    </span>
                    <span className="font-serif text-lg text-ink leading-tight">
                      {f.number}
                    </span>
                  </div>
                  <div className="mt-3 font-serif text-lg text-ink leading-tight group-hover:text-saffron transition">
                    {f.name.replace(/^The\s/, "").replace(/\sFamily$/, "").replace(/\sDynasty.*/, "")}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-[0.24em] text-ink-50">
                    {f.type}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed cards */}
      <section className="relative">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          {FAMOUS_FAMILIES.map((f, i) => (
            <div key={f.id} id={f.id}>
              <FamilyCard f={f} i={i} />
            </div>
          ))}
        </div>
      </section>

      {/* Historic royal houses */}
      <section className="relative py-24 bg-tulsi text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-marigold">Historic royal houses</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
              Beyond the ten —
              <span className="italic text-marigold"> palaces </span>
              still standing.
            </h2>
            <p className="mt-6 text-ivory/70 text-lg font-light max-w-md leading-relaxed">
              India remains home to numerous historic royal lineages whose cultural influence
              continues today, from Rajasthan's Aravallis to the Himalayas of Sikkim and Ladakh.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-16">
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              {HISTORIC_ROYAL_HOUSES.map((h, i) => (
                <li
                  key={h.name}
                  data-testid={FAMOUS.royalItem(i)}
                  className="border-b border-ivory/15 py-5"
                >
                  <a
                    href={h.wiki}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-baseline justify-between gap-4 group"
                  >
                    <span className="font-serif text-xl md:text-2xl group-hover:text-marigold transition">
                      {h.name}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-ivory/60 text-right">
                      {h.region}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Profile schema */}
      <section className="relative py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">The archive schema</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
              What every
              <span className="italic text-tulsi"> family </span>
              profile holds.
            </h2>
            <p className="mt-6 text-ink-50 text-lg font-light max-w-md leading-relaxed">
              A consistent schema across IndianFamilyTree.com — so a Kalpathy tharavad and the Wadiyar
              dynasty of Mysore can live side-by-side in the same living archive.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7 lg:pl-16">
            <dl className="divide-y divide-ivory-300 border-y border-ivory-300">
              {PROFILE_FIELDS.map((p) => (
                <div key={p.field} className="grid grid-cols-12 gap-4 py-5">
                  <dt className="col-span-12 md:col-span-4 font-serif text-xl text-ink">{p.field}</dt>
                  <dd className="col-span-12 md:col-span-8 text-ink-50 leading-relaxed">{p.desc}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Closing */}
      <section className="relative py-24 bg-ivory-100/60 border-t border-ivory-300">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Preserving family heritage</span>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl tracking-tight text-ink leading-tight">
              Every family — royal, entrepreneurial, artistic, or rooted in a village — holds a unique story.
            </h2>
            <p className="mt-6 text-ink-50 text-lg font-light leading-relaxed">
              India's history is not only recorded in palaces, archives and monuments, but also in
              family photographs, oral traditions, handwritten letters, migration journeys and cherished
              memories passed from one generation to the next. IndianFamilyTree.com — part of the
              SocialPulse heritage ecosystem — helps you document your tree, ancestral home, village
              history and personal narratives so India's living archive keeps growing.
            </p>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
